import React from 'react'
import styled, { ThemeProvider } from 'styled-components'
import { Link, graphql } from 'gatsby'

import Layout, { extractData, THEMES } from '../components/layout'
import { Page, Routing, SSRAware, posterWidthAndHeight, Row, Column } from '../components/utils'
import { Movie, Cinema, CinemaId, Model, CinemasMap } from '../model/model'

import Section from '../components/section';
import createStore from '../model/create-store'
import { Provider, connect } from 'react-redux';
import ConnectedUpdater from '../components/updater';
import RImage from '../components/image';
import { Dispatch } from 'redux';
import { Action } from '../model/actions';
import { Tracking, isNotNull } from '../model/utils';
import Option from '../components/option';

const Card = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
  align-items: center;
  @media (min-width: 700px) {
    padding: 5px 10px;
    max-width: 300px;
  }
`
const Poster = styled(RImage)`
  display: flex;
  ${posterWidthAndHeight(120)}
  object-fit: cover;
  @media (min-width: 700px) {
    ${posterWidthAndHeight(130)}
  }
`
const MovieName = styled.h3`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-transform: uppercase;
  text-align: center;
  font-size: 1rem;
  margin: 0.5rem;
  font-weight: 300;
  @media (min-width: 700px) {
    line-height: 1.3rem;
  }
`

interface MovieCardProps {
  movie: Movie
  shows: string[]
}

const MovieCard: React.SFC<MovieCardProps & SSRAware> = ({isSSR, movie, shows}) => (
  <Card>
    <Poster mode={isSSR ? 'stub' : 'lazy'} image={movie.poster} alt={`Poster film ${movie.title}`}
      sizes={[{viewport: 700, size: 120}]}
      maxSize={150} />
    <MovieName>
      {shows.length === 0
      ? null
      : shows[0] === '*'
        ? '*'
        : <Row>
            {shows.map (s =>
              <Option key={s} style={{margin: '0 5px 5px 5px'}}>{s}</Option>
            )}
          </Row>
      }
      {movie.title}
    </MovieName>
  </Card>
)

interface MoviesSection {
  name: string
  cards: MovieCardProps[]
}
interface StaticProps {
  mainCinemaId: CinemaId
  cinemas: Cinema[]
}
interface DynamicProps {
  sections: MoviesSection[]
}
type PageProps = StaticProps & DynamicProps

const Group = styled(Row)`
flex-wrap: wrap;
justify-content: center;
margin-bottom: 2rem;
@media (min-width: 700px) {
  justify-content: flex-start;
}
`
const GroupName = styled.div`
color: ${props => props.theme.colors.highlight};
display: flex;
flex-grow: 0;
align-items: center;
justify-content: center;
text-align: center;
width: 100%;
margin-bottom: 1rem;
@media (min-width: 700px) {
  justify-content: flex-start;
  text-align: left;
  width: 25%;
}
`
const GroupMovie = styled(Column)`
width: 100%;
@media (min-width: 700px) {
  width: 25%;
}
`

const PageTitle = styled.h1`
text-align: center;
color: ${props => props.theme.colors.highlight};
`

const PageContent = styled.div`
text-align: center;
& p {
  font-size: 1.1rem;
  line-height: 1.7rem;
}
& li {
  list-style: none;
  font-weight: bold;
}
`

const TwoEuroPage: React.SFC<PageProps & SSRAware> = ({isSSR, cinemas, mainCinemaId, sections}) => {
  return (
    <Layout showBackButton={false} cinemas={cinemas} title="Cinema a 2€">
      <Page>
        <Section>
          <PageTitle>Cinema a 2 €uro {new Date().getFullYear()}</PageTitle>
          { mainCinemaId === 'mds'
          ? <PageContent>
              <p>
                Anche quest'estate torna "Cinema a 2 €uro" <br/>
                la nostra esclusiva rassegna con i migliori film dell'anno a soli €2!
              </p>
              <ul>
                <li>Tutti i giorni alle 18:00 e 21:00</li>
              </ul>
            </PageContent>
          : <PageContent>
              <p>
                Per la prima volta anche al Cinema Odeon 6 arriva il "Cinema a 2 €uro"  <br/>
                La rassegna dei migliori film dell'anno a soli €2!
              </p>
            </PageContent>
          }

        { sections.map(section =>
          <Group key={section.name}>
            <GroupName dangerouslySetInnerHTML={{__html: section.name}}></GroupName>
            { section.cards.map ((card, idx) =>
              <GroupMovie key={idx}>
                { card.movie.slug === 'film-a-sorpresa'
                ? <MovieCard isSSR={isSSR} movie={card.movie} shows={card.shows}/>
                : <Link to={Routing.toMoviePage(card.movie)}
                  onClick={() => {Tracking.trackNavigation('film-2-euro/main', card.movie.title)}}>
                    <MovieCard isSSR={isSSR} movie={card.movie} shows={card.shows}/>
                  </Link>
              }
              </GroupMovie>
            )}
          </Group>
        )}
        </Section>
      </Page>
    </Layout>
  )
}

const MOVIES_2EURO: CinemasMap<{name: string[], movieInfos: string[]}[]> = {
  mds: [
    {
      name:['SABATO 1 LUGLIO',
      'DOMENICA 2 LUGLIO',
      'LUNEDÌ 3 LUGLIO',],
      movieInfos: ['ant-man-and-the-wasp-quantumania', 'il-gatto-con-gli-stivali-2', 'mia']
    },
    {
      name:['MARTEDÌ 4 LUGLIO',
      'MERCOLEDÌ 5 LUGLIO',
      'GIOVEDÌ 6 LUGLIO',
      'VENERDÌ 7 LUGLIO',],
      movieInfos: ['avatar-la-via-dellacqua', 'the-whale', 'il-grande-giorno']
    },
    {
      name:['SABATO 8 LUGLIO',
      'DOMENICA 9 LUGLIO',
      'LUNEDÌ 10 LUGLIO',
      'MARTEDÌ 11 LUGLIO',],
      movieInfos: ['black-panther-wakanda-forever', 'asterix-obelix-il-regno-di-mezzo', 'la-stranezza']
    },
    {
      name:['MERCOLEDÌ 12 LUGLIO',
      'GIOVEDÌ 13 LUGLIO',
      'VENERDÌ 14 LUGLIO',
      'SABATO 15 LUGLIO',],
      movieInfos: ['dungeons-dragons-lonore-dei-ladri', 'mai-cosi-vicino', 'il-colibri']
    },
    {
      name:['DOMENICA 16 LUGLIO',
      'LUNEDÌ 17 LUGLIO',
      'MARTEDÌ 18 LUGLIO',
      'MERCOLEDÌ 19 LUGLIO',],
      movieInfos: ['creed-iii', 'mavka-e-la-foresta-incantata', 'tramite-amicizia']
    },
    {
      name:['GIOVEDÌ 20 LUGLIO',
      'VENERDÌ 21 LUGLIO',
      'SABATO 22 LUGLIO',
      'DOMENICA 23 LUGLIO',],
      movieInfos: ['shazam-furia-degli-dei', 'ticket-to-paradise', 'le-otto-montagne']
    },
    {
      name:['LUNEDÌ 24 LUGLIO',
      'MARTEDÌ 25 LUGLIO',
      'MERCOLEDÌ 26 LUGLIO',
      'GIOVEDÌ 27 LUGLIO',],
      movieInfos: ['fast-x', 'super-mario-bros-il-film', 'mixed-by-erry']
    },
    {
      name:['VENERDÌ 28 LUGLIO',
      'SABATO 29 LUGLIO',
      'DOMENICA 30 LUGLIO',
      'LUNEDÌ 31 LUGLIO',],
      movieInfos: ['transformers-il-risveglio', 'everything-everywhere-all-at-once', 'lultima-notte-di-amore']
    },
    {
      name:['MARTEDÌ 1 AGOSTO',
      'MERCOLEDÌ 2 AGOSTO',
      'GIOVEDÌ 3 AGOSTO',
      'VENERDÌ 4 AGOSTO',],
      movieInfos: ['john-wick-4', 'la-sirenetta', 'i-migliori-giorni']
    },
    {
      name:['SABATO 5 AGOSTO',
      'DOMENICA 6 AGOSTO',
      'LUNEDÌ 7 AGOSTO',
      'MARTEDÌ 8 AGOSTO',],
      movieInfos: ['guardiani-della-galassia-vol-3', 'air-la-storia-del-grande-salto', 'quando']
    },
    {
      name:['MERCOLEDÌ 9 AGOSTO',
      'GIOVEDÌ 10 AGOSTO',
      'VENERDÌ 11 AGOSTO',
      'SABATO 12 AGOSTO',],
      movieInfos: ['the-flash', 'film-a-sorpresa', 'grazie-ragazzi']
    },
    {
      name:['DOMENICA 13 AGOSTO',
      'LUNEDÌ 14 AGOSTO',
      'MARTEDÌ 15 AGOSTO',
      'MERCOLEDÌ 16 AGOSTO',],
      movieInfos: ['spider-man-spider-verse', 'film-a-sorpresa', 'scordato']
    },
    {
      name:['GIOVEDÌ 17 AGOSTO',
      'VENERDÌ 18 AGOSTO',
      'SABATO 19 AGOSTO',
      'DOMENICA 20 AGOSTO',],
      movieInfos: ['film-a-sorpresa', 'film-a-sorpresa', 'siccita']
    },
    {
      name:
      ['LUNEDÌ 21 AGOSTO',
      'MARTEDÌ 22 AGOSTO',
      'MERCOLEDÌ 23 AGOSTO',
      'GIOVEDÌ 24 AGOSTO',],
      movieInfos: ['film-a-sorpresa', 'film-a-sorpresa', 'film-a-sorpresa']
    },
  ],
  odeon6: [
  ]
}

const mapModelToProps = (model: Model, ownProps: StaticProps): DynamicProps & SSRAware => {
  if (model.page.id !== 'events') {
    throw new Error('Unexpected state when computing Events props: ' + model.page.id)
  }
  return {
    isSSR: model.isSSR,
    sections: MOVIES_2EURO[ownProps.mainCinemaId].map(s => ({
      name: s.name.join('<br/>'),
      cards: s.movieInfos
        .map(slugCode => {
          const [slug, ...showTokens] = slugCode.split(' ')
          const movie = model.extraMovies.find(m => slug === m.slug)
          return movie == null
          ? null
          : {
              movie: movie,
              shows: showTokens
            }
        })
        .filter(isNotNull)
    }))
  }
}

const mapDispatchToIntents = (dispatch: Dispatch<Action>) => ({
})

const ConnectedPage = connect(
  mapModelToProps,
  mapDispatchToIntents
)(TwoEuroPage as any)

const WrappedPage: React.SFC<any> = ( query ) => {
  const { mainCinemaId, cinemas } = extractData(query)
  const store = createStore('events', cinemas, mainCinemaId, false)

  return (
    <ThemeProvider theme={THEMES[mainCinemaId]}>
      <Provider store={store}>
        <>
          <ConnectedUpdater/>
          <ConnectedPage cinemas={cinemas} mainCinemaId={mainCinemaId} />
        </>
      </Provider>
    </ThemeProvider>
  )
}

export default WrappedPage

export const query = graphql`
query TwoEurosPageQuery {
  ...alldata
}
`
